
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { Loader2, Calendar } from "lucide-react";
import { GalleryPhoto } from "./carousel/types";
import { DatePicker } from "@/components/ui/datepicker";
import { format } from "date-fns";

interface PhotoTitleEditDialogProps {
  photo: GalleryPhoto;
  onTitleUpdated: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function PhotoTitleEditDialog({ 
  photo, 
  open: externalOpen, 
  onOpenChange: externalOnOpenChange, 
  onTitleUpdated 
}: PhotoTitleEditDialogProps) {
  const { language } = useLanguage();
  const [title, setTitle] = useState("");
  const [uploadDate, setUploadDate] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [open, setOpen] = useState(false);

  // Control open state from both internal and external sources
  const isOpen = externalOpen !== undefined ? externalOpen : open;
  const onOpenChange = externalOnOpenChange || setOpen;

  // Update the title and date state when the photo changes
  useEffect(() => {
    if (photo) {
      setTitle(photo.title);
      setUploadDate(photo.created_at ? new Date(photo.created_at) : undefined);
    }
  }, [photo]);

  const handleSave = async () => {
    if (!photo || !title.trim()) return;
    
    setIsSaving(true);
    try {
      const updateData: Record<string, any> = { title: title.trim() };
      
      // Only update the date if it has been changed
      if (uploadDate) {
        updateData.created_at = uploadDate.toISOString();
      }
      
      const { error } = await supabase
        .from('gallery_photos')
        .update(updateData)
        .eq('id', photo.id);
        
      if (error) throw error;
      
      toast.success(t('gallery.photoUpdateSuccess', language) || 'Photo updated successfully');
      onTitleUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating photo:", error);
      toast.error(t('gallery.photoUpdateFailed', language) || 'Failed to update photo');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDateEdit = () => {
    setIsEditingDate(!isEditingDate);
  };

  return (
    <>
      <h3 
        className="text-sm font-medium truncate cursor-pointer hover:text-primary transition-colors"
        onClick={() => onOpenChange(true)}
      >
        {photo.title || t('gallery.untitledPhoto', language) || 'Untitled Photo'}
      </h3>
      
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gallery.editPhotoDetails', language) || 'Edit Photo Details'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="photo-title">{t('gallery.photoTitle', language)}</Label>
              <Input
                id="photo-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('gallery.enterPhotoTitle', language) || 'Enter photo title'}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="photo-date">{t('gallery.uploadDate', language) || 'Upload Date'}</Label>
              <div className="flex items-center gap-2">
                {isEditingDate ? (
                  <DatePicker 
                    date={uploadDate} 
                    onDateChange={(date) => {
                      setUploadDate(date);
                      setIsEditingDate(false);
                    }}
                    className="w-full"
                  />
                ) : (
                  <div className="flex w-full items-center justify-between border border-input bg-background px-3 py-2 rounded-md">
                    <span>
                      {uploadDate ? format(uploadDate, 'PPP') : t('gallery.noDateSet', language) || 'No date set'}
                    </span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="icon" 
                      onClick={toggleDateEdit}
                    >
                      <Calendar className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="w-full h-60 overflow-hidden rounded-md">
              <img 
                src={photo.url} 
                alt={photo.title} 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              {t('common.cancel', language)}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving || !title.trim()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.saving', language) || 'Saving...'}
                </>
              ) : (
                t('common.save', language) || 'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
